import { Injectable, NgZone, inject } from '@angular/core'
import * as Sentry from '@sentry/angular'

import { type HubClientPacket, type LotStatus } from '../../core/models/hub-client/hub-client-packet.model'
import { type LotModel } from '../../modules/public/models/lot.model'
import { getBuyerBidStatusKey, type BuyerBidStatus } from '../utils/buyer-bid-status'

interface BuyerBidObservation {
    observedAtMs: number
    sentTimeUtc: string | null
    messageId: string | null
    auctionId: number | null
    buyerId: number
    lotId: number
    activityStatus: number | null | undefined
    packetStatus: LotStatus
    currentPrice: number | null
    increment: number | null
    winningBidderId: number | null | undefined
    isUsersBid: boolean
    hasUserBid: boolean
    buyerInBidders: boolean
    biddersCount: number
    lastBidder: number | null
    status: BuyerBidStatus
}

@Injectable({
    providedIn: 'root'
})
export class BuyerBidDiagnosticsService {
    private static readonly buyerBidStatusMismatchMessage = 'Buyer bid status mismatch'
    private static readonly buyerBidStatusMismatchTag = 'Buyer Dashboard - buyer bid status mismatch'
    private static readonly buyerBidStatusMismatchFingerprint = 'buyer-bid-status-mismatch'
    private static readonly buyerBidStatusDedupWindowMs = 15000

    private ngZone = inject(NgZone)
    private lastBuyerBidObservation: BuyerBidObservation | null = null
    private lastBuyerBidDiagnosticAt = new Map<string, number>()

    recordBidUpdate(
        lot: LotModel | null | undefined,
        packetStatus: LotStatus,
        signal: HubClientPacket,
        buyerId?: number | null,
        lastBidder?: number | null,
        auctionId?: number | null
    ) {
        if (buyerId == null || !lot?.id) {
            this.reset()
            return
        }

        const nextObservation = this.createBuyerBidObservation(
            lot,
            packetStatus,
            signal,
            buyerId,
            lastBidder ?? null,
            auctionId ?? null
        )
        const previousObservation = this.lastBuyerBidObservation

        this.lastBuyerBidObservation = nextObservation

        if (
            !previousObservation ||
            previousObservation.lotId !== nextObservation.lotId ||
            previousObservation.buyerId !== nextObservation.buyerId
        ) {
            return
        }

        this.addBuyerBidStatusBreadcrumb(previousObservation, nextObservation)

        const mismatchReasons = this.getBuyerBidMismatchReasons(previousObservation, nextObservation)
        if (!mismatchReasons.length || !this.shouldCaptureBuyerBidMismatch(nextObservation, mismatchReasons)) {
            return
        }

        this.ngZone.runOutsideAngular(() => {
            Sentry.withScope((scope) => {
                scope.setLevel('warning')
                scope.setFingerprint([
                    BuyerBidDiagnosticsService.buyerBidStatusMismatchFingerprint,
                    String(nextObservation.auctionId ?? 'unknown'),
                    String(nextObservation.lotId),
                    mismatchReasons.join('|')
                ])
                scope.setTag('diagnostic_event', BuyerBidDiagnosticsService.buyerBidStatusMismatchTag)
                scope.setTag('auction_ui', 'Buyer Dashboard')
                scope.setTag('warning_type', mismatchReasons.join(','))
                scope.setTag('auction_id', String(nextObservation.auctionId ?? 'unknown'))
                scope.setTag('lot_id', String(nextObservation.lotId))
                scope.setTag('buyer_bid_status', nextObservation.status)
                scope.setTag('packet_status', String(nextObservation.packetStatus))
                scope.setContext('buyer_bid_status_transition', {
                    previous: this.getBuyerBidObservationContext(previousObservation),
                    next: this.getBuyerBidObservationContext(nextObservation)
                })

                Sentry.captureMessage(BuyerBidDiagnosticsService.buyerBidStatusMismatchMessage)
            })
        })
    }

    reset() {
        this.lastBuyerBidObservation = null
    }

    clearForTest() {
        this.lastBuyerBidObservation = null
        this.lastBuyerBidDiagnosticAt.clear()
    }

    private addBuyerBidStatusBreadcrumb(
        previousObservation: BuyerBidObservation,
        nextObservation: BuyerBidObservation
    ) {
        if (previousObservation.status === nextObservation.status) {
            return
        }

        this.ngZone.runOutsideAngular(() => {
            Sentry.addBreadcrumb({
                type: 'default',
                category: 'buyer-bid-status',
                level: 'info',
                message: `Buyer bid status changed from ${previousObservation.status} to ${nextObservation.status}`,
                data: {
                    auctionId: nextObservation.auctionId,
                    lotId: nextObservation.lotId,
                    previousStatus: previousObservation.status,
                    nextStatus: nextObservation.status,
                    previousWinningBidderId: previousObservation.winningBidderId,
                    nextWinningBidderId: nextObservation.winningBidderId,
                    previousCurrentPrice: previousObservation.currentPrice,
                    nextCurrentPrice: nextObservation.currentPrice,
                    packetStatus: nextObservation.packetStatus
                }
            })
        })
    }

    private getBuyerBidMismatchReasons(
        previousObservation: BuyerBidObservation,
        nextObservation: BuyerBidObservation
    ): string[] {
        const reasons = new Set<string>()

        if (previousObservation.sentTimeUtc && nextObservation.sentTimeUtc) {
            const previousSentAt = Date.parse(previousObservation.sentTimeUtc)
            const nextSentAt = Date.parse(nextObservation.sentTimeUtc)
            if (!Number.isNaN(previousSentAt) && !Number.isNaN(nextSentAt) && nextSentAt < previousSentAt) {
                reasons.add('out-of-order-packet')
            }
        }

        if (nextObservation.isUsersBid && !nextObservation.buyerInBidders) {
            reasons.add('winning-buyer-missing-from-bidder-list')
        }

        const buyerStatusFlipped = previousObservation.status !== nextObservation.status
        const buyerStatusTracked = previousObservation.status !== 'none' && nextObservation.status !== 'none'

        if (buyerStatusFlipped && buyerStatusTracked) {
            if (previousObservation.currentPrice === nextObservation.currentPrice) {
                reasons.add('status-flip-without-price-change')
            }

            if (previousObservation.winningBidderId === nextObservation.winningBidderId) {
                reasons.add('status-flip-without-winner-change')
            }
        }

        return Array.from(reasons)
    }

    private shouldCaptureBuyerBidMismatch(nextObservation: BuyerBidObservation, mismatchReasons: string[]): boolean {
        const diagnosticKey = [
            nextObservation.auctionId ?? 'unknown',
            nextObservation.lotId,
            nextObservation.status,
            nextObservation.currentPrice ?? 'none',
            nextObservation.winningBidderId ?? 'none',
            mismatchReasons.join('|')
        ].join(':')
        const now = Date.now()
        const lastCapturedAt = this.lastBuyerBidDiagnosticAt.get(diagnosticKey)

        if (lastCapturedAt && now - lastCapturedAt < BuyerBidDiagnosticsService.buyerBidStatusDedupWindowMs) {
            return false
        }

        this.lastBuyerBidDiagnosticAt.set(diagnosticKey, now)
        return true
    }

    private createBuyerBidObservation(
        lot: LotModel,
        packetStatus: LotStatus,
        signal: HubClientPacket,
        buyerId: number,
        lastBidder: number | null,
        auctionId: number | null
    ): BuyerBidObservation {
        const biddersIds = Array.isArray(lot.biddersIds) ? lot.biddersIds : []
        const isUsersBid = Boolean(lot.isUsersBid)
        const hasUserBid = Boolean(lot.hasUserBid)
        const buyerInBidders = biddersIds.includes(buyerId)
        const sentTimeUtc = this.normalizeSentTimeUtc(signal.Header.SentTimeUtc)

        return {
            observedAtMs: Date.now(),
            sentTimeUtc,
            messageId: signal.Header.MessageId,
            auctionId,
            buyerId,
            lotId: lot.id!,
            activityStatus: lot.activityStatus,
            packetStatus,
            currentPrice: lot.currentPrice ?? null,
            increment: lot.increment ?? null,
            winningBidderId: lot.winningBidderId,
            isUsersBid,
            hasUserBid,
            buyerInBidders,
            biddersCount: biddersIds.length,
            lastBidder,
            status: getBuyerBidStatusKey(lot)
        }
    }

    private getBuyerBidObservationContext(observation: BuyerBidObservation) {
        return {
            messageId: observation.messageId,
            sentTimeUtc: observation.sentTimeUtc,
            observedAtMs: observation.observedAtMs,
            buyerId: observation.buyerId,
            activityStatus: observation.activityStatus,
            packetStatus: observation.packetStatus,
            status: observation.status,
            isUsersBid: observation.isUsersBid,
            hasUserBid: observation.hasUserBid,
            buyerInBidders: observation.buyerInBidders,
            biddersCount: observation.biddersCount,
            winningBidderId: observation.winningBidderId,
            currentPrice: observation.currentPrice,
            increment: observation.increment,
            lastBidder: observation.lastBidder
        }
    }

    private normalizeSentTimeUtc(sentTimeUtc: Date | string | null | undefined): string | null {
        if (!sentTimeUtc) {
            return null
        }

        const normalizedDate = new Date(sentTimeUtc)
        return Number.isNaN(normalizedDate.getTime()) ? null : normalizedDate.toISOString()
    }
}
